import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import CompaniesApi from '../api/companies';

export default function CompanyDropdownSelection({ selectedCompany: onSelect, showAll, style }) {
  const [companies, setCompanies] = useState([]);
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState([]); // array of company objects

  const containerRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    CompaniesApi.getAll()
      .then((res) => setCompanies(res.data))
      .catch(() => {});
  }, []);

  // Group companies by organization
  const { orgGroups, ungrouped } = useMemo(() => {
    const orgMap = new Map();
    const ungrouped = [];
    companies.forEach((c) => {
      if (c.organization?.id) {
        if (!orgMap.has(c.organization.id)) {
          orgMap.set(c.organization.id, { id: c.organization.id, name: c.organization.name, companies: [] });
        }
        orgMap.get(c.organization.id).companies.push(c);
      } else {
        ungrouped.push(c);
      }
    });
    const orgGroups = [...orgMap.values()].sort((a, b) => a.name.localeCompare(b.name, 'he'));
    return { orgGroups, ungrouped };
  }, [companies]);

  const searchTerm = search.trim().toLowerCase();

  // Filter companies and orgs by search
  const filteredOrgs = useMemo(() => {
    if (!searchTerm) return orgGroups;
    return orgGroups
      .map((org) => ({
        ...org,
        companies: org.companies.filter(
          (c) => c.name.toLowerCase().includes(searchTerm) || String(c.id).includes(searchTerm)
        ),
      }))
      .filter((org) => org.companies.length > 0);
  }, [orgGroups, searchTerm]);

  const filteredUngrouped = useMemo(() => {
    if (!searchTerm) return ungrouped;
    return ungrouped.filter(
      (c) => c.name.toLowerCase().includes(searchTerm) || String(c.id).includes(searchTerm)
    );
  }, [ungrouped, searchTerm]);

  const isChecked = useCallback((c) => selected.some((s) => s.id === c.id), [selected]);

  const isOrgChecked = useCallback(
    (org) => org.companies.length > 0 && org.companies.every((c) => isChecked(c)),
    [isChecked]
  );

  const isOrgIndeterminate = useCallback(
    (org) => org.companies.some((c) => isChecked(c)) && !org.companies.every((c) => isChecked(c)),
    [isChecked]
  );

  const allCompaniesSelected = companies.length > 0 && companies.every((c) => isChecked(c));

  const commit = useCallback(
    (next) => {
      setSelected(next);
      onSelect(next);
    },
    [onSelect]
  );

  const toggleCompany = useCallback(
    (c) => {
      const next = isChecked(c) ? selected.filter((s) => s.id !== c.id) : [...selected, c];
      commit(next);
    },
    [selected, isChecked, commit]
  );

  const toggleOrg = useCallback(
    (org) => {
      const allSelected = isOrgChecked(org);
      const orgIds = new Set(org.companies.map((c) => c.id));
      const withoutOrg = selected.filter((s) => !orgIds.has(s.id));
      const next = allSelected ? withoutOrg : [...withoutOrg, ...org.companies];
      commit(next);
    },
    [selected, isOrgChecked, commit]
  );

  const toggleAll = useCallback(() => {
    commit(allCompaniesSelected ? [] : [...companies]);
  }, [companies, allCompaniesSelected, commit]);

  const triggerLabel =
    selected.length === 0
      ? 'בחר חברה'
      : selected.length === 1
      ? selected[0].name
      : `${selected.length} חברות נבחרו`;

  useEffect(() => {
    const handler = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
        setSearch('');
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  useEffect(() => {
    if (open) inputRef.current?.focus();
  }, [open]);

  const hasResults = filteredOrgs.length > 0 || filteredUngrouped.length > 0;

  return (
    <div ref={containerRef} style={{ position: 'relative', minWidth: 190, ...style }}>
      {/* Trigger */}
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          padding: '7px 12px',
          border: open ? '1.5px solid #422AFB' : '1.5px solid #e0e0e0',
          borderRadius: 8,
          backgroundColor: '#fff',
          cursor: 'pointer',
          fontSize: 14,
          fontWeight: 500,
          color: selected.length > 0 ? '#222' : '#999',
          width: '100%',
          boxShadow: open ? '0 0 0 3px rgba(66,42,251,0.08)' : '0 1px 3px rgba(0,0,0,0.08)',
          transition: 'all 0.15s',
          direction: 'rtl',
        }}
      >
        <span style={{ flex: 1, textAlign: 'right', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {triggerLabel}
        </span>
        {selected.length > 0 && (
          <span style={{
            backgroundColor: '#422AFB', color: '#fff', borderRadius: '50%',
            width: 18, height: 18, fontSize: 11, display: 'flex', alignItems: 'center',
            justifyContent: 'center', flexShrink: 0, fontWeight: 700,
          }}>
            {selected.length}
          </span>
        )}
        <span style={{ color: '#aaa', fontSize: 11, flexShrink: 0 }}>{open ? '▲' : '▼'}</span>
      </button>

      {open && (
        <div style={{
          position: 'absolute', top: 'calc(100% + 6px)', right: 0, zIndex: 1000,
          backgroundColor: '#fff', border: '1.5px solid #e0e0e0', borderRadius: 10,
          boxShadow: '0 8px 24px rgba(0,0,0,0.12)', width: 300, overflow: 'hidden',
        }}>
          {/* Search */}
          <div style={{ padding: '10px 12px', borderBottom: '1px solid #f0f0f0' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, direction: 'rtl' }}>
              <span style={{ color: '#aaa', fontSize: 13 }}>🔍</span>
              <input
                ref={inputRef}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onKeyDown={(e) => e.key === 'Escape' && (setOpen(false), setSearch(''))}
                placeholder="חיפוש חברה..."
                style={{ flex: 1, border: 'none', outline: 'none', fontSize: 13, color: '#333', backgroundColor: 'transparent', direction: 'rtl' }}
              />
              {search && (
                <button onClick={() => setSearch('')} style={{ border: 'none', background: 'none', cursor: 'pointer', color: '#aaa', fontSize: 16, padding: 0, lineHeight: 1 }}>×</button>
              )}
            </div>
          </div>

          {/* List */}
          <div style={{ maxHeight: 300, overflowY: 'auto' }}>
            {/* Select all */}
            {showAll && !searchTerm && (
              <div
                onClick={toggleAll}
                style={{
                  display: 'flex', alignItems: 'center', gap: 10, padding: '9px 14px',
                  cursor: 'pointer', direction: 'rtl',
                  backgroundColor: allCompaniesSelected ? '#f5f3ff' : '#fafafa',
                  borderBottom: '1px solid #f0f0f0',
                }}
              >
                <input type="checkbox" readOnly checked={allCompaniesSelected}
                  style={{ accentColor: '#422AFB', width: 15, height: 15, cursor: 'pointer', flexShrink: 0 }} />
                <span style={{ fontSize: 14, fontWeight: 600, color: '#222' }}>הכל</span>
              </div>
            )}

            {!hasResults && (
              <div style={{ padding: 14, color: '#999', fontSize: 13, textAlign: 'center' }}>לא נמצאו חברות</div>
            )}

            {/* Org groups */}
            {filteredOrgs.map((org) => {
              const orgChecked = isOrgChecked(org);
              const orgIndet = isOrgIndeterminate(org);
              return (
                <React.Fragment key={`org-${org.id}`}>
                  {/* Org header row */}
                  <div
                    onClick={() => toggleOrg(org)}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 10, padding: '8px 14px',
                      cursor: 'pointer', direction: 'rtl',
                      backgroundColor: orgChecked ? '#f5f3ff' : '#f8f8fc',
                      borderTop: '1px solid #f0f0f0',
                    }}
                  >
                    <OrgCheckbox checked={orgChecked} indeterminate={orgIndet} />
                    <span style={{ fontSize: 12, fontWeight: 700, color: '#422AFB', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {org.name}
                    </span>
                    <span style={{ fontSize: 11, color: '#aaa', flexShrink: 0 }}>{org.companies.length}</span>
                  </div>

                  {/* Companies under org */}
                  {org.companies.map((c) => {
                    const checked = isChecked(c);
                    return (
                      <div
                        key={c.id}
                        onClick={(e) => { e.stopPropagation(); toggleCompany(c); }}
                        style={{
                          display: 'flex', alignItems: 'center', gap: 10,
                          padding: '8px 14px 8px 28px',
                          cursor: 'pointer', direction: 'rtl',
                          backgroundColor: checked ? '#f5f3ff' : 'transparent',
                          transition: 'background 0.1s',
                        }}
                      >
                        <input type="checkbox" readOnly checked={checked}
                          style={{ accentColor: '#422AFB', width: 14, height: 14, cursor: 'pointer', flexShrink: 0 }} />
                        <div style={{ flex: 1, overflow: 'hidden' }}>
                          <div style={{ fontSize: 13, fontWeight: checked ? 600 : 400, color: '#222', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {c.name}
                          </div>
                          {c.employeesCount != null && (
                            <div style={{ fontSize: 11, color: '#999', marginTop: 1 }}>{c.employeesCount} עובדים</div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </React.Fragment>
              );
            })}

            {/* Ungrouped companies */}
            {filteredUngrouped.map((c) => {
              const checked = isChecked(c);
              return (
                <div
                  key={c.id}
                  onClick={() => toggleCompany(c)}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 10, padding: '9px 14px',
                    cursor: 'pointer', direction: 'rtl',
                    backgroundColor: checked ? '#f5f3ff' : 'transparent',
                    transition: 'background 0.1s',
                    borderTop: filteredOrgs.length > 0 ? '1px solid #f0f0f0' : undefined,
                  }}
                >
                  <input type="checkbox" readOnly checked={checked}
                    style={{ accentColor: '#422AFB', width: 15, height: 15, cursor: 'pointer', flexShrink: 0 }} />
                  <span style={{ fontSize: 15, flexShrink: 0 }}>🏢</span>
                  <div style={{ flex: 1, overflow: 'hidden' }}>
                    <div style={{ fontSize: 13, fontWeight: checked ? 600 : 400, color: '#222', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {c.name}
                    </div>
                    {c.employeesCount != null && (
                      <div style={{ fontSize: 11, color: '#999', marginTop: 1 }}>{c.employeesCount} עובדים</div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Footer */}
          <div style={{ padding: '7px 14px', borderTop: '1px solid #f0f0f0', fontSize: 11, color: '#ccc', display: 'flex', gap: 12 }}>
            <span>esc close</span>
          </div>
        </div>
      )}
    </div>
  );
}

// Checkbox that supports indeterminate state (can't do it with readOnly alone)
function OrgCheckbox({ checked, indeterminate }) {
  const ref = useRef(null);
  useEffect(() => {
    if (ref.current) ref.current.indeterminate = indeterminate;
  }, [indeterminate]);
  return (
    <input
      ref={ref}
      type="checkbox"
      readOnly
      checked={checked}
      style={{ accentColor: '#422AFB', width: 15, height: 15, cursor: 'pointer', flexShrink: 0 }}
    />
  );
}
